<!--
  Client Portal v2: Orders (PR D4).

  Rebuilt to the v2 design (planning .../handoff_2026-06-24/
  2026-06-24-client-portal-v2/design/sections-core.jsx, the Orders function):
    - header: "Add New Order" for a caller holding order.create (card #566;
      opens /portal/orders/new, the shared NewOrderFlow)
    - toolbar: search + All-stores + All-statuses + Export CSV
    - columns: Order / Shopper (NAME) / Department / Store / Total + Status
    - expandable rows: Items + Shipping + View-full-order / Request-a-refund
    - refund flow: DISPLAY-ONLY STUB (Jake-confirmed). The form opens and the
      pending/approved/denied banners render from the order's refund_status
      (read-only). Submit is INERT -- it sets an optimistic local "(not yet
      wired)" pending state and never calls a mutating endpoint, because no
      refund-request backend model/endpoint exists in this PR.
    - footer "Refunds are a request" banner
    - empty / loading / error states

  Wired to GET /api/portal/orders (api/src/endpoints/portal.py). The endpoint
  scopes strictly to the caller's Client (403 on a non-client caller) and
  serializes money at the cents boundary; this page formats cents -> dollars
  at render.
-->
<template>
  <v-container fluid class="portal-orders" data-testid="portal-page-orders">
    <div class="orders-head">
      <h1 class="text-h5 mb-1">Orders</h1>
      <!-- Staff order entry for a Client Admin (card #566): moved here from
           the deleted legacy dashboard. Outside the loading / error branches
           so placing an order never depends on the list loading. Gated on
           the same capability as the /portal/orders/new route. -->
      <PortalButton
        v-if="canCreateOrder"
        variant="primary"
        data-testid="orders-add-order"
        @click="addNewOrder"
      >
        <template #icon><v-icon size="18">add</v-icon></template>
        Add New Order
      </PortalButton>
    </div>
    <p class="text-body-2 text-medium-emphasis mb-4">
      Every order across your stores. Track status, look up a shopper's order,
      request a refund, or export.
    </p>

    <v-progress-linear
      v-if="loading"
      indeterminate
      color="primary"
      data-testid="orders-loading"
    />

    <v-alert
      v-else-if="errorMsg"
      type="error"
      variant="tonal"
      data-testid="orders-error"
    >
      {{ errorMsg }}
    </v-alert>

    <template v-else>
      <!-- Toolbar: search + store filter + status filter + Export CSV -->
      <div class="orders-toolbar mb-4" data-testid="orders-toolbar">
        <v-text-field
          v-model="search"
          density="compact"
          variant="solo-filled"
          flat
          hide-details
          prepend-inner-icon="search"
          placeholder="Search order #, shopper, department..."
          class="orders-toolbar__search"
          data-testid="orders-search"
        />
        <v-select
          v-model="storeFilter"
          :items="storeFilterItems"
          density="compact"
          variant="outlined"
          hide-details
          class="orders-toolbar__select"
          data-testid="orders-filter-store"
        />
        <v-select
          v-model="statusFilter"
          :items="statusFilterItems"
          density="compact"
          variant="outlined"
          hide-details
          class="orders-toolbar__select"
          data-testid="orders-filter-status"
        />
        <v-spacer />
        <PortalButton
          variant="ghost"
          data-testid="orders-export-csv"
          @click="exportCsv"
        >
          <template #icon><v-icon size="18">download</v-icon></template>
          Export CSV
        </PortalButton>
      </div>

      <v-card>
        <!-- Uppercase column header row (v2 grid) -->
        <div
          class="orders-grid orders-grid--head"
          data-testid="orders-column-header"
        >
          <span>Order</span>
          <span>Shopper</span>
          <span>Department</span>
          <span>Store</span>
          <span class="text-right">Total</span>
          <span>Status</span>
          <span></span>
        </div>

        <div
          v-if="!filteredOrders.length"
          class="orders-empty pa-6 text-body-2 text-medium-emphasis"
          data-testid="orders-empty"
        >
          {{
            orders.length
              ? "No orders match your filters."
              : "No orders yet. When your shoppers start ordering, every order across your stores shows up here."
          }}
        </div>

        <template v-for="order in filteredOrders" :key="order.id">
          <div
            class="orders-grid orders-row"
            :data-testid="`orders-row-${order.id}`"
            @click="toggle(order.id)"
          >
            <span class="orders-cell--mono">{{ order.order_number }}</span>
            <span :data-testid="`orders-shopper-${order.id}`">{{
              order.shopper_name || order.placed_by_email || "Unknown shopper"
            }}</span>
            <span class="text-medium-emphasis">{{
              order.department || "--"
            }}</span>
            <span class="orders-cell--ellipsis text-medium-emphasis">{{
              order.store_name || "--"
            }}</span>
            <span class="orders-cell--mono text-right font-weight-medium">{{
              formatDollars(order.total_cents)
            }}</span>
            <span class="d-flex align-center" style="gap: 6px">
              <span
                class="orders-status-chip"
                :class="statusChipClass(order.status)"
                :data-testid="`orders-status-${order.id}`"
              >
                {{ statusLabel(order.status) }}
              </span>
              <v-icon
                v-if="order.refund_status"
                size="14"
                color="grey"
                title="Has a refund request"
                >undo</v-icon
              >
            </span>
            <span class="text-right">
              <v-icon size="20" color="grey">{{
                expandedId === order.id ? "expand_less" : "expand_more"
              }}</v-icon>
            </span>
          </div>

          <!-- Expanded detail: Items + Shipping + actions -->
          <div
            v-if="expandedId === order.id"
            class="orders-expand"
            :data-testid="`orders-expand-${order.id}`"
          >
            <div class="orders-expand__grid">
              <div :data-testid="`orders-items-${order.id}`">
                <div class="orders-sublabel">Items</div>
                <div
                  v-if="!(order.items && order.items.length)"
                  class="text-body-2 text-medium-emphasis"
                >
                  No line items recorded.
                </div>
                <div
                  v-for="(item, i) in order.items"
                  :key="i"
                  class="text-body-2"
                >
                  {{ item.quantity }} &times; {{ item.product_name || "Item" }}
                  <span v-if="item.variant" class="text-medium-emphasis"
                    >({{ item.variant }})</span
                  >
                </div>
              </div>

              <div :data-testid="`orders-shipping-${order.id}`">
                <div class="orders-sublabel">Shipping</div>
                <div v-if="order.shipping" class="text-body-2">
                  <div>{{ order.shipping.name || order.shopper_name }}</div>
                  <div class="text-medium-emphasis">
                    {{ shippingAddress(order.shipping) }}
                  </div>
                  <div class="text-medium-emphasis">
                    Ship status: {{ statusLabel(order.shipping.status) }}
                  </div>
                </div>
                <div v-else class="text-body-2 text-medium-emphasis">
                  No shipping details on file.
                </div>

                <!-- Order actions -->
                <div class="mt-3">
                  <div class="d-flex mt-1" style="gap: 8px">
                    <PortalButton
                      variant="ghost"
                      size="sm"
                      class="orders-action-btn"
                      :data-testid="`orders-view-full-${order.id}`"
                      @click="viewFullOrder(order)"
                    >
                      <template #icon><v-icon size="16">open_in_new</v-icon></template>
                      View full order
                    </PortalButton>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </v-card>

    </template>

    <!-- Full-order overlay opened by "View full order". Shows client-safe
         fields only (via the portal-safe OrderDetailDialog -> OrderDetailView).
         Reuses the row's already-fetched /api/portal/orders payload. -->
    <!-- No data-testid here: OrderDetailDialog's root v-dialog owns
         data-testid="portal-order-dialog"; a fallthrough attr of the same name
         would be swallowed. Target "portal-order-dialog" in tests. -->
    <OrderDetailDialog v-model="detailDialogOpen" :order="detailOrder" />
  </v-container>
</template>

<script>
import { describeError } from "@/utils/safeErrorLog";
import axios from "@/axios";
import OrderDetailDialog from "@/components/portal/OrderDetailDialog.vue";
import PortalButton from "@/components/portal/PortalButton.vue";
import { usePortalScopeStore } from "@/stores/portalScope";
import { usePermissions } from "@/composables/usePermissions";

// Portal status vocabulary -> human label.
const STATUS_LABELS = {
  pending: "Placed",
  in_production: "In production",
  shipped: "Shipped",
  delivered: "Delivered",
  cancelled: "Cancelled",
};

// Status -> status-chip style class. Matches the v2 design's StatusChip
// fg/bg pairs (info / warning / success / neutral) with AA-safe full-contrast
// text tokens on the soft backgrounds. Replaces the old Vuetify tonal chip
// whose derived foreground rendered too muted (Jake's review, nit #1).
const STATUS_CHIP_CLASS = {
  pending: "orders-status-chip--info",
  in_production: "orders-status-chip--warning",
  shipped: "orders-status-chip--success",
  delivered: "orders-status-chip--success",
  cancelled: "orders-status-chip--neutral",
};

export default {
  name: "PortalOrders",
  components: { OrderDetailDialog, PortalButton },
  setup() {
    // The GLOBAL header store scope (ADR 0055). When a store is selected in
    // the topbar pill, this page passes its store_id to the server so the
    // list is scoped server-side (this endpoint previously had NO server-side
    // store filter -- the store dropdown was client-side only).
    const scope = usePortalScopeStore();
    const { can } = usePermissions();
    return { scope, can };
  },
  data() {
    return {
      orders: [],
      loading: true,
      errorMsg: "",
      // Monotonic fetch counter for the store-scope re-fetch race guard.
      _reqSeq: 0,
      // Toolbar
      search: "",
      storeFilter: "All stores",
      statusFilter: "All statuses",
      statusFilterItems: [
        "All statuses",
        "Placed",
        "In production",
        "Shipped",
        "Delivered",
        "Cancelled",
      ],
      // Expand local UI state
      expandedId: null,
      // "View full order" overlay state.
      detailDialogOpen: false,
      detailOrder: null,
    };
  },
  computed: {
    canCreateOrder() {
      return this.can("order.create");
    },
    storeFilterItems() {
      const names = new Set();
      for (const o of this.orders) {
        if (o.store_name) names.add(o.store_name);
      }
      return ["All stores", ...Array.from(names).sort()];
    },
    filteredOrders() {
      const q = this.search.trim().toLowerCase();
      return this.orders.filter((o) => {
        if (
          this.storeFilter !== "All stores" &&
          o.store_name !== this.storeFilter
        ) {
          return false;
        }
        if (
          this.statusFilter !== "All statuses" &&
          this.statusLabel(o.status) !== this.statusFilter
        ) {
          return false;
        }
        if (!q) return true;
        const hay = [
          o.order_number,
          o.shopper_name,
          o.placed_by_email,
          o.department,
          o.store_name,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      });
    },
  },
  created() {
    this.fetchOrders();
  },
  watch: {
    // Re-fetch server-side whenever the global header store scope changes so
    // the list re-scopes to (or away from) the selected store.
    "scope.selectedStoreId"() {
      this.fetchOrders();
    },
  },
  methods: {
    async fetchOrders() {
      // Request-sequence guard: rapid scope changes (dropdown / leaderboard)
      // fire overlapping fetches whose responses can arrive out of order (the
      // aggregate query is heavier than a single-store one). Stamp each fetch
      // and drop a response that a newer fetch has superseded, so a stale
      // payload never clobbers the current scope's data. (inflight-guard
      // pattern.)
      const seq = ++this._reqSeq;
      this.loading = true;
      this.errorMsg = "";
      try {
        const resp = await axios.get("/api/portal/orders", {
          params: this.scope.storeIdParams,
        });
        if (seq !== this._reqSeq) return;
        this.orders = resp?.data?.orders ?? [];
      } catch (e) {
        if (seq !== this._reqSeq) return;
        this.errorMsg = "Could not load your orders.";
      } finally {
        if (seq === this._reqSeq) this.loading = false;
      }
    },
    addNewOrder() {
      this.$router.push({ name: "portal_new_order" });
    },
    toggle(id) {
      this.expandedId = this.expandedId === id ? null : id;
    },
    statusLabel(status) {
      return STATUS_LABELS[status] || status || "Placed";
    },
    statusChipClass(status) {
      return STATUS_CHIP_CLASS[status] || "orders-status-chip--neutral";
    },
    shippingAddress(ship) {
      const parts = [
        ship.address1,
        ship.address2,
        [ship.city, ship.state].filter(Boolean).join(", "),
        ship.postal_code,
      ].filter(Boolean);
      return parts.join(" ");
    },
    viewFullOrder(order) {
      // Open the client-safe order-detail overlay for this row. The row
      // already carries the full /api/portal/orders per-order payload, so no
      // extra fetch is needed. OrderDetailDialog renders only client-safe
      // fields (no distributor-internal data).
      this.detailOrder = order;
      this.detailDialogOpen = true;
    },
    async exportCsv() {
      // Trigger a browser download of /api/portal/orders/export. The backend
      // applies the same client-scope and store-scope filters as the JSON list
      // endpoint and returns a text/csv with Content-Disposition: attachment.
      try {
        const resp = await axios.get("/api/portal/orders/export", {
          params: this.scope.storeIdParams,
          responseType: "blob",
        });
        const url = URL.createObjectURL(new Blob([resp.data], { type: "text/csv" }));
        const a = document.createElement("a");
        a.href = url;
        a.download = "orders.csv";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (e) {
        // Non-fatal: the user can retry; log to console for debugging.
        console.error("orders CSV export failed", describeError(e));
      }
    },
    formatDollars(cents) {
      return `$${((cents || 0) / 100).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },
  },
};
</script>

<style scoped>
.orders-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--tf-sp-3, 12px);
  flex-wrap: wrap;
}
.orders-toolbar {
  display: flex;
  align-items: center;
  gap: var(--tf-sp-3, 12px);
  flex-wrap: wrap;
}
.orders-toolbar__search {
  flex: 1 1 240px;
  min-width: 200px;
}
.orders-toolbar__select {
  flex: 0 0 auto;
  min-width: 160px;
}
.orders-grid {
  display: grid;
  grid-template-columns: 120px 1.2fr 1fr 1fr 110px 130px 36px;
  align-items: center;
  gap: var(--tf-sp-3, 12px);
  padding: 12px 18px;
}
.orders-grid--head {
  background: var(--tf-surface-alt, #fafbfc);
  border-bottom: 1px solid var(--tf-border, #e0e0e0);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: var(--tf-fg-4, #757575);
}
.orders-row {
  border-top: 1px solid var(--tf-border, #e0e0e0);
  cursor: pointer;
}
.orders-row:hover {
  background: var(--tf-surface-alt, #fafbfc);
}
.orders-cell--mono {
  font-family: var(--tf-font-mono, monospace);
  font-size: 12px;
}
.orders-cell--ellipsis {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.text-right {
  text-align: right;
}
.orders-expand {
  border-top: 1px solid var(--tf-border, #e0e0e0);
  background: var(--tf-surface-alt, #fafbfc);
  padding: 14px 18px 18px;
}
.orders-expand__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}
.orders-sublabel {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: var(--tf-fg-4, #757575);
  margin-bottom: 8px;
}
.orders-refund-form {
  background: var(--tf-surface, #fff);
  border: 1px solid var(--tf-border, #e0e0e0);
  border-radius: var(--tf-radius-sm, 8px);
  padding: 14px;
}

/* Status chip (v2 design): explicit AA-safe fg on soft bg, pill radius.
   Replaces the Vuetify tonal chip whose derived foreground was too muted. */
.orders-status-chip {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.3px;
  line-height: 1.4;
  padding: 3px 10px;
  border-radius: var(--tf-radius-pill, 9999px);
  white-space: nowrap;
}
.orders-status-chip--info {
  color: var(--tf-chip-info-fg, #1565c0);
  background: var(--tf-chip-info-bg, #e3f2fd);
}
.orders-status-chip--warning {
  color: var(--tf-warning-dark, #8a6d3b);
  background: var(--tf-warning-soft, #fff8e1);
}
.orders-status-chip--success {
  color: var(--tf-success-dark, #2e7d32);
  background: var(--tf-success-soft, #e6f7ed);
}
.orders-status-chip--neutral {
  color: var(--tf-fg-3, #616161);
  background: var(--tf-chip-neutral-bg, #f1f3f5);
}

/* Expanded-row action buttons. The shared PortalButton now bakes in the pill
   radius (v2 Btn); this class is a layout hook + a harmless radius backstop
   so the shape holds even under a theme override. */
.orders-action-btn {
  border-radius: var(--tf-radius-pill, 9999px);
}
@media (max-width: 700px) {
  /* Keep all 7 cells in their own columns on narrow screens. Tightening
     the fixed columns (rather than dropping to 3 and letting 7 cells wrap
     into a broken grid) preserves row alignment; the Expanded panel stacks.
     Full mobile redesign of tabular surfaces is deferred (see CLAUDE.md). */
  .orders-grid,
  .orders-grid--head {
    grid-template-columns: 86px 1.1fr 0.9fr 0.9fr 84px 96px 28px;
    gap: var(--tf-sp-2, 8px);
    padding-left: 12px;
    padding-right: 12px;
  }
  .orders-expand__grid {
    grid-template-columns: 1fr;
  }
}
</style>
